import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from '@/hooks/useReducedMotion';

/**
 * Magnetic button: subtly pulls toward cursor on hover.
 * Renders <Link> if `to` is provided, <a> if `href`, else <button>.
 */
export function MagneticButton({
  children,
  href,
  to,
  variant = 'primary', // primary | ghost | outline
  className = '',
  strength = 18,
  external = false,
  ...rest
}) {
  const ref = useRef(null);
  const innerRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el || reduced) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      const dx = (x / rect.width) * strength;
      const dy = (y / rect.height) * strength;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      if (innerRef.current) {
        innerRef.current.style.transform = `translate(${dx * 0.4}px, ${dy * 0.4}px)`;
      }
    };
    const onLeave = () => {
      el.style.transform = '';
      if (innerRef.current) innerRef.current.style.transform = '';
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [reduced, strength]);

  const base =
    'group relative inline-flex items-center gap-3 rounded-full px-6 py-3.5 text-sm font-medium tracking-tight transition-[transform,background,border-color,box-shadow] duration-300 ease-out will-change-transform';
  const variants = {
    primary:
      'bg-violet text-white shadow-[0_0_0_0_rgba(108,92,231,0)] hover:shadow-[0_10px_40px_-10px_rgba(108,92,231,0.7)] border border-white/10',
    ghost:
      'text-white/90 hover:text-white border border-white/[0.12] bg-white/[0.02] hover:bg-white/[0.05]',
    outline:
      'text-white border border-white/15 hover:border-white/40',
  };

  const cls = `${base} ${variants[variant] || variants.primary} ${className}`;

  const inner = (
    <span ref={innerRef} className="inline-flex items-center gap-3 transition-transform duration-300">
      {children}
    </span>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={cls} {...rest}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        ref={ref}
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className={cls}
        {...rest}
      >
        {inner}
      </a>
    );
  }
  return (
    <button ref={ref} type="button" className={cls} {...rest}>
      {inner}
    </button>
  );
}
